import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// components
import Breadcrumbs from '../../../components/breadcrumbs';
import { ButtonField, TextField } from '../../../components/hook-form';
import Dialog from '../../../components/dialog';
// @form
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// utils
import { REGEXP_SPECIAL } from '../../../utils/regexp';
// styles
import { Container, Message, ButtonWrapper, PrimaryButton, TertiaryButton } from '../../../styles';
// apis
import { existsCategory, createDisplayNames, updateDisplayNames } from '../../../apis';

// ----------------------------------------------------------------------

const schema = yup.object().shape({
  category: yup.string()
    .required()
    .trim()
    .min(1, '형식을 확인해 주세요.')
    .max(20, '형식을 확인해 주세요.')
    .matches(REGEXP_SPECIAL, '형식을 확인해 주세요.'),
  waterTemperature: yup.string()
    .required()
    .trim()
    .min(1, '형식을 확인해 주세요.')
    .max(20, '형식을 확인해 주세요.')
    .matches(REGEXP_SPECIAL, '형식을 확인해 주세요.'),
  waterTemperatureUnit: yup.string()
    .required()
    .trim()
    .min(1, '형식을 확인해 주세요.')
    .max(10, '형식을 확인해 주세요.')
});

// ----------------------------------------------------------------------

interface Props {
  category?: string;
  waterTemperature?: string;
  waterTemperatureUnit?: string;
};

// ----------------------------------------------------------------------

export default function FormPage() {
  const navigate  = useNavigate();
  const create = useLocation().pathname.split('/')[3] === 'create' ? true : false;
  const state = useLocation().state;

  const {
    control,
    getValues,
    setFocus,
    setError,
    clearErrors,
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      category: state?.category || '',
      waterTemperature: state?.waterTemperature || '',
      waterTemperatureUnit: state?.waterTemperatureUnit || ''
    }
  });

  const [ checked, setChecked ] = useState({result: null, message: null});
  const [ checkedCategory, setCheckedCategory ] = useState(null);
  const [ message, setMessage ] = useState(null);

  const handleChecked = async () => {
    const category = getValues('category');
    if ( category === '' ) {
      setError('category', {type: 'required'});
      setFocus('category');
    } else if ( category.length < 1 || category.length > 20 || !REGEXP_SPECIAL.test(category) ) {
      setError('category', {type: 'matches', message: '형식을 확인해 주세요.'});
      setFocus('category');
    } else {
      clearErrors('category');
      await existsCategory(category)
        .then((response) => {
          setChecked({result: response, message: response ? '이미 등록된 표시명입니다.' : '사용하실 수 있는 표시명입니다.'});
          setCheckedCategory(category);
        })
        .catch(() => {
          setChecked({result: null, message: '나중에 다시 시도해 주세요.'});
        });
    }
  };

  const onSubmit = async (data: Props) => {
    if ( create ) {
      if ( checked === null || checkedCategory !== data.category ) {
        setChecked({result: null, message: '중복 체크 후 진행하실 수 있습니다.'});
      } else if ( checked.result === false ) {
        await createDisplayNames(data.category, data.waterTemperature, data.waterTemperatureUnit)
          .then(() => navigate('/iot/display-names', {state: {submit: true}}))
          .catch(() => setMessage('표시명을 등록 할 수 없습니다.\n나중에 다시 시도해 주세요.'));
      }
    } else {
      await updateDisplayNames(state.displayNameId, data.waterTemperature, data.waterTemperatureUnit)
        .then(() => navigate('/iot/display-names', {state: {submit: true}}))
        .catch(() => setMessage('표시명을 수정 할 수 없습니다.\n나중에 다시 시도해 주세요.'));
    }
  };

  return (
    <Container>
      <Breadcrumbs
        heading={`표시명 ${create ? '등록' : '수정'}`}
        links={[
          {name: 'IoT 장비 관리'},
          {name: 'IoT 장비 그룹', link: '/iot/groups'},
          {name: '표시명 관리', link: '/iot/display-names'},
          {name: `표시명 ${create ? '등록' : '수정'}`}
        ]}
      />

      <form onSubmit={ handleSubmit(onSubmit) }>
        <ButtonField
          name='category'
          control={control}
          label='분류'
          essential={create ? true : false}
          description={create ? '1~20자 사이 한글, 영문 대소문자, 숫자, 특수기호 !@&()-_+[]{}. 를 사용해 주세요.' : null}
          exists={checked}
          disabled={create ? false : true}
          action={
            <TertiaryButton type='button' disabled={create ? false : true} onClick={() => handleChecked()}>중복 확인</TertiaryButton>
          }
          maxLength={20}
        />

        <TextField
          type='text'
          name='waterTemperature'
          label='수온 표시명'
          control={control}
          essential={true}
          description='1~20자 사이 한글, 영문 대소문자, 숫자, 특수기호 !@&()-_+[]{}. 를 사용해 주세요.'
          maxLength={20}
        />

        <TextField
          type='text'
          name='waterTemperatureUnit'
          label='수온 단위'
          essential={true}
          control={control}
          description='1~10자 사이로 입력해 주세요.'
          maxLength={10}
        />

        <Message $visible={message ? true : false}>{ message }</Message>

        <ButtonWrapper>
          <PrimaryButton type='submit'>{create ? '등록' : '수정'}</PrimaryButton>
          <TertiaryButton type='button' onClick={() => navigate('/iot/groups')}>취소</TertiaryButton>
        </ButtonWrapper>
      </form>
    </Container>
  );
}